import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FilePlus2, Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, KpiStrip, MetricCard, Td, Th } from '@/components/erp/primitives'
import { FilterBar, MultiSelect, SearchInput } from '@/shared/ui/filters'
import { EmptyState, ErrorStrip, LoadingRows } from '@/shared/ui/states'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { fmtNumber } from '@/shared/lib/format'
import { useElectionContext } from '@/shared/election/context'
import { NoElectionSelected } from '@/shared/election/ElectionSelector'
import {
  candidacySummary, listCandidacies,
  type Candidacy, type CandidacySeatSummary,
} from './api'
import { NominationChip, PartyChip, SEAT_ORDER, label, options } from './vocab'
import { CandidacyDrawer } from './CandidacyDrawer'
import { CandidacyForm } from './CandidacyForm'
import { ImportWizard } from './ImportWizard'

/**
 * The ballot register — who is standing, for what, where.
 *
 * ## Election-scoped, and the scoping is not optional
 *
 * The election comes from the shell's selector, not from a filter on this page. With none
 * chosen the page says so in a sentence rather than rendering an empty grid: "no rows" and
 * "no election chosen" look identical otherwise, and only one of them is the user's to fix.
 *
 * ## The readiness strip is the point of the page
 *
 * A count of candidacies answers nothing. What a desk needs before polling day is *how much
 * of the ballot exists* — "MCA: 3,100 across 1,088 of 1,450 wards" says three-hundred-odd
 * wards have no candidates loaded, which is the actionable number. That is why the strip
 * carries `races` (distinct units) beside the totals.
 */
export function CandidacyPage() {
  const { election, electionId } = useElectionContext()
  const [params, setParams] = useSearchParams()

  const [search, setSearch] = useState(params.get('q') ?? '')
  const debounced = useDebounced(search, 300)
  const [seatType, setSeatType] = useState<string[]>(params.getAll('seatType'))
  const [status, setStatus] = useState<string[]>(params.getAll('status'))
  const [onBallotOnly, setOnBallotOnly] = useState(params.get('onBallot') === 'true')

  const [rows, setRows] = useState<Candidacy[] | null>(null)
  const [summary, setSummary] = useState<CandidacySeatSummary[]>([])
  const [error, setError] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const importOpen = params.get('import') === '1'
  const openId = params.get('open')

  const filter = useMemo(() => ({
    seatType: seatType[0] ?? null,
    status: status[0] ?? null,
    onBallot: onBallotOnly ? true : null,
    search: debounced || null,
    size: 300,
  }), [seatType, status, onBallotOnly, debounced])

  /*
     `electionId` IS in the dependency list, and that is the whole point.

     It is null on first render while the election store is still loading, and a loader
     bound to `[]` would capture that null and never fire again — the page would sit empty
     forever with nothing saying why. Four station-mates pages and the incident wizard
     shipped exactly this.
  */
  const load = useCallback(async () => {
    if (!electionId) { setRows(null); setSummary([]); return }
    setError(null)
    try {
      const [list, seats] = await Promise.all([
        listCandidacies(electionId, filter),
        candidacySummary(electionId),
      ])
      setRows(list)
      setSummary(seats)
    } catch (err) {
      setError(explain(err))
      setRows([])
    }
  }, [electionId, filter])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    const next = new URLSearchParams(params)
    next.delete('seatType'); seatType.forEach((v) => next.append('seatType', v))
    next.delete('status'); status.forEach((v) => next.append('status', v))
    if (debounced) next.set('q', debounced); else next.delete('q')
    if (onBallotOnly) next.set('onBallot', 'true'); else next.delete('onBallot')
    setParams(next, { replace: true })
    // `params`/`setParams` omitted deliberately: including them re-runs this on the write it
    // just made, which is an infinite loop rather than a subtle bug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatType, status, onBallotOnly, debounced])

  if (!electionId) return <NoElectionSelected what="A ballot" />

  // Narrow locally for the second and later selections — the server takes one code per axis.
  const visible = (rows ?? []).filter((r) =>
    (seatType.length === 0 || seatType.includes(r.seatTypeCode))
    && (status.length === 0 || status.includes(r.nominationStatusCode)))

  const totals = summary.reduce((acc, s) => ({
    total: acc.total + s.total,
    onBallot: acc.onBallot + s.onBallot,
    independents: acc.independents + s.independents,
    races: acc.races + s.races,
  }), { total: 0, onBallot: 0, independents: 0, races: 0 })

  return (
    <div className="space-y-3">
      <KpiStrip>
        <MetricCard label="Candidacies" value={fmtNumber(totals.total)}
          sub={election ? election.name : undefined} accent />
        <MetricCard label="On the ballot" value={fmtNumber(totals.onBallot)}
          sub="Cleared or certified" />
        <MetricCard label="Races with candidates" value={fmtNumber(totals.races)}
          sub="Distinct seats loaded" />
        <MetricCard label="Independents" value={fmtNumber(totals.independents)} />
      </KpiStrip>

      <SeatReadiness rows={summary} />

      <FilterBar
        activeCount={(seatType.length > 0 ? 1 : 0) + (status.length > 0 ? 1 : 0)
          + (onBallotOnly ? 1 : 0) + (debounced ? 1 : 0)}
        right={(
          <>
            <button type="button" onClick={() => setFormOpen(true)}
              className="erp-btn-primary h-9 px-3 inline-flex items-center gap-1.5 text-erp-sm">
              <FilePlus2 className="h-3.5 w-3.5" /> Nominate a candidate
            </button>
            {/* Offered to everyone; the server's refusal names the authority. */}
            <button type="button"
              onClick={() => setParams((p) => { p.set('import', '1'); return p }, { replace: true })}
              className="erp-btn h-9 px-3 inline-flex items-center gap-1.5 text-erp-sm">
              <Upload className="h-3.5 w-3.5" /> Import a list
            </button>
          </>
        )}
      >
        <SearchInput value={search} onChange={setSearch}
          placeholder="Ballot name or certificate…" />
        <MultiSelect label="Seat" options={options(SEAT_ORDER)}
          selected={seatType} onChange={setSeatType} />
        <MultiSelect label="Status"
          options={options(['PROSPECTIVE', 'NOMINATED', 'CLEARED', 'CERTIFICATE_ISSUED',
            'WITHDRAWN', 'DISQUALIFIED'])}
          selected={status} onChange={setStatus} />
        <label className="h-9 px-3 inline-flex items-center gap-2 border border-input rounded-md bg-background text-erp-sm">
          <input type="checkbox" checked={onBallotOnly}
            onChange={(e) => setOnBallotOnly(e.target.checked)} />
          On the ballot only
        </label>
      </FilterBar>

      <ErrorStrip message={error} className="rounded-md border" />

      <ErpTable head={(
        <>
          <Th className="text-right w-12">#</Th>
          <Th>Candidate</Th>
          <Th>Seat</Th>
          <Th>Where</Th>
          <Th>Party</Th>
          <Th>Status</Th>
          <Th>Certificate</Th>
          <Th>Source</Th>
        </>
      )}>
        {rows === null ? <LoadingRows colSpan={8} label="Loading the ballot…" /> : (
          visible.length === 0 ? (
            <tr><td colSpan={8}>
              <EmptyState
                title="No candidacies yet"
                hint="Nominate a candidate, or import a nomination list. Nothing can be tallied until a ballot exists."
              />
            </td></tr>
          ) : visible.map((r) => (
            <tr key={r.id}
              onClick={() => setParams((p) => { p.set('open', r.id); return p }, { replace: true })}
              className={cn('cursor-pointer hover:bg-muted/40',
                // Off the ballot is dimmed, not hidden: a desk reconciling a results form
                // against the paper needs to see that somebody was disqualified.
                !r.onBallot && 'text-muted-foreground')}>
              <Td className="text-right tabular-nums">{r.ballotOrder ?? '—'}</Td>
              <Td className="max-w-64">
                <div className="truncate font-medium">{r.ballotName}</div>
                {/* Only when they differ — otherwise it is the same name twice. */}
                {r.candidateName && r.candidateName !== r.ballotName && (
                  <div className="text-erp-xs text-muted-foreground truncate">
                    In the register as {r.candidateName}
                  </div>
                )}
              </Td>
              <Td className="whitespace-nowrap">{label(r.seatTypeCode)}</Td>
              <Td className="max-w-48">
                <span className="truncate block" title={r.unitName ?? undefined}>
                  {r.unitName ?? '—'}
                </span>
              </Td>
              <Td>
                <PartyChip name={r.partyName} shortName={r.partyShortName}
                  colourHex={r.partyColourHex} independent={r.independent} />
              </Td>
              <Td><NominationChip code={r.nominationStatusCode} /></Td>
              <Td className="font-mono text-erp-xs">{r.certificateNumber ?? '—'}</Td>
              <Td className="text-erp-xs text-muted-foreground">{label(r.sourceCode)}</Td>
            </tr>
          ))
        )}
      </ErpTable>

      <ImportWizard
        open={importOpen}
        electionId={electionId}
        onClose={() => setParams((p) => { p.delete('import'); return p }, { replace: true })}
        onApplied={load}
      />

      <CandidacyForm
        open={formOpen}
        electionId={electionId}
        onClose={() => setFormOpen(false)}
        onSaved={async () => { setFormOpen(false); await load() }}
      />

      <CandidacyDrawer
        id={openId}
        onClose={() => setParams((p) => { p.delete('open'); return p }, { replace: true })}
        onChanged={load}
      />
    </div>
  )
}

/**
 * How complete each seat's ballot is.
 *
 * Deliberately a table and not a chart. The question is "which seat is missing candidates",
 * and six labelled rows with exact counts answer it better than six bars — the numbers
 * differ by three orders of magnitude (one presidential race, 1,450 MCA races), so any
 * shared scale flattens five of the six to nothing.
 */
function SeatReadiness({ rows }: { rows: CandidacySeatSummary[] }) {
  if (rows.length === 0) return null
  const bySeat = new Map(rows.map((r) => [r.seatTypeCode, r]))
  return (
    <div className="erp-card px-3 py-2.5">
      <div className="text-erp-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
        Ballot readiness
      </div>
      <div className="axis-table-scroll">
        <table className="w-full border-collapse min-w-max">
          <thead><tr>
            <th className="erp-grid-header text-left">Seat</th>
            <th className="erp-grid-header text-left">Contested at</th>
            <th className="erp-grid-header text-right">Candidacies</th>
            <th className="erp-grid-header text-right">On the ballot</th>
            <th className="erp-grid-header text-right">Races loaded</th>
            <th className="erp-grid-header text-right">Independents</th>
          </tr></thead>
          <tbody>
            {SEAT_ORDER.map((seat) => {
              const s = bySeat.get(seat)
              return (
                <tr key={seat} className={cn(!s && 'text-muted-foreground')}>
                  <td className="erp-grid-cell">{label(seat)}</td>
                  <td className="erp-grid-cell text-erp-xs text-muted-foreground">
                    {label(s?.requiredLevel ?? null)}
                  </td>
                  <td className="erp-grid-cell text-right tabular-nums">
                    {s ? fmtNumber(s.total) : '—'}
                  </td>
                  <td className="erp-grid-cell text-right tabular-nums">
                    {s ? fmtNumber(s.onBallot) : '—'}
                  </td>
                  <td className="erp-grid-cell text-right tabular-nums">
                    {s ? fmtNumber(s.races) : '—'}
                  </td>
                  <td className="erp-grid-cell text-right tabular-nums">
                    {s ? fmtNumber(s.independents) : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-erp-xs text-muted-foreground/80">
        "Races loaded" counts distinct seats with at least one candidacy — a general election
        has one presidential race, 47 county races, 290 constituencies and 1,450 wards, so
        this is the number that says what is still missing.
      </p>
    </div>
  )
}
